import { Card, Chip, Paper, Box } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

export const GlassCard = styled(Card)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: "8px",
  border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
  background:
    theme.palette.mode === "light"
      ? alpha(theme.palette.background.paper, 0.96)
      : alpha(theme.palette.background.paper, 0.82),
  boxShadow: theme.hr?.shadow?.soft || "none",
  transition: "box-shadow 180ms ease, border-color 180ms ease",

  "&:hover": {
    borderColor: alpha(theme.palette.primary.main, 0.25),
  },

  "& .MuiCardHeader-root": {
    paddingTop: 14,
    paddingBottom: 10,
  },

  "& .MuiCardContent-root": {
    paddingTop: 0,
    paddingBottom: 16,
  },

  "&::before": {
    content: '""',
    position: "absolute",
    left: 14,
    right: 14,
    top: 0,
    height: 1,
    pointerEvents: "none",
    background: `linear-gradient(90deg,
      ${alpha(theme.palette.primary.main, 0)} 0%,
      ${alpha(theme.palette.primary.main, 0.22)} 24%,
      ${alpha(theme.palette.secondary.main, 0.18)} 76%,
      ${alpha(theme.palette.secondary.main, 0)} 100%)`,
  },

  "&::after": { display: "none" },
}));

export const AccentBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== "tone",
})(({ theme, tone = "primary" }) => {
  const pal = theme.palette[tone] || theme.palette.primary;
  return {
    height: 4,
    width: "100%",
    borderRadius: 999,
    background: `linear-gradient(90deg, ${pal.main}, ${pal.light})`,
    opacity: theme.palette.mode === "light" ? 0.9 : 0.75,
  };
});

export const StepPill = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  borderRadius: "8px",
  fontWeight: 800,
  border: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
  backgroundColor: alpha(
    theme.palette.background.paper,
    theme.palette.mode === "light" ? 0.9 : 0.78
  ),
  transition:
    "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background 180ms ease",
  "& .MuiChip-label": {
    paddingLeft: 10,
    paddingRight: 10,
    fontSize: "0.86rem",
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .MuiChip-icon": {
    marginLeft: 8,
    marginRight: -4,
  },
  ...(active && {
    borderColor: alpha(theme.palette.primary.main, 0.48),
    background: `linear-gradient(135deg,
      ${alpha(theme.palette.primary.main, 0.10)} 0%,
      ${alpha(theme.palette.primary.light, 0.06)} 100%)`,
    boxShadow: `0 6px 14px ${alpha(theme.palette.primary.main, 0.1)}`,
  }),
}));

export const TimelineConnector = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "completed",
})(({ theme, active, completed }) => ({
  width: 40,
  height: 2,
  borderRadius: 999,
  backgroundColor: active
    ? theme.palette.primary.main
    : completed
      ? theme.palette.success.main
      : alpha(theme.palette.primary.main, 0.18),
  position: "relative",
  transition: "background-color 200ms ease",
  "&::after": active
    ? {
        content: '""',
        position: "absolute",
        inset: -2,
        borderRadius: 999,
        background: alpha(theme.palette.primary.main, 0.16),
      }
    : {},
}));

export const ProgressIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== "progress",
})(({ theme, progress }) => ({
  position: "relative",
  height: 6,
  borderRadius: 999,
  backgroundColor: alpha(theme.palette.primary.main, 0.10),
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    height: "100%",
    width: `${Math.max(0, Math.min(100, Number(progress) || 0))}%`,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    borderRadius: 999,
    transition: "width 420ms ease",
  },
}));

/* Required by QuestionsCard.jsx */
export const QuestionCard = styled(Paper, {
  shouldForwardProp: (prop) =>
    prop !== "editable" && prop !== "haschanges" && prop !== "excluded",
})(({ theme, editable, haschanges, excluded }) => {
  const isEditable = Boolean(editable);
  const hasChanges = haschanges === true || haschanges === "true";
  const isExcluded = excluded === true || excluded === "true";

  return {
    borderRadius: "8px",
    overflow: "hidden",
    position: "relative",
    border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
    background:
      theme.palette.mode === "light"
        ? `linear-gradient(135deg,
            ${alpha(theme.palette.background.paper, 0.92)} 0%,
            ${alpha(theme.palette.background.paper, 0.98)} 100%)`
        : alpha(theme.palette.background.paper, 0.78),
    transition: "box-shadow 0.18s ease, border-color 0.18s ease",

    ...(hasChanges && {
      borderColor: alpha(theme.palette.info.main, 0.5),
      boxShadow: `0 8px 18px ${alpha(theme.palette.info.main, 0.10)}`,
    }),

    ...(isExcluded && {
      borderColor: alpha(theme.palette.warning.main, 0.35),
      background:
        theme.palette.mode === "light"
          ? `linear-gradient(135deg,
              ${alpha(theme.palette.warning.light, 0.08)} 0%,
              ${alpha(theme.palette.background.paper, 0.98)} 100%)`
          : alpha(theme.palette.background.paper, 0.72),
    }),

    ...(!isEditable && { opacity: 0.9 }),

    "&:hover": {
      borderColor: isEditable
        ? alpha(theme.palette.primary.main, 0.28)
        : alpha(theme.palette.divider, 0.14),
      boxShadow: isEditable ? theme.shadows[1] : "none",
    },

    "&::before": hasChanges
      ? {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          background: `linear-gradient(to bottom, ${theme.palette.info.main}, ${theme.palette.info.light})`,
        }
      : {},
  };
});

/* Required by LeftSidebar.jsx + QuestionsCard.jsx */
export const ReferenceBadge = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "3px 10px",
  borderRadius: "8px",
  backgroundColor: alpha(theme.palette.info.main, 0.1),
  color:
    theme.palette.mode === "light" ? theme.palette.info.dark : theme.palette.info.light,
  fontSize: "0.75rem",
  fontWeight: 900,
  border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
}));
